import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { TASK_TYPE_LABELS } from '@claimup/shared/constants/assignments';
import api, { getErrorMessage } from '../../../services/api';
import AIAgentReviewSettings from './AIAgentReviewSettings';
import AIAgentReviewCase from './AIAgentReviewCase';
import AIAgentLoHagoYo from './AIAgentLoHagoYo';
import AIAgentConsult from './AIAgentConsult';
import PaymentPlanCollapsible from '../../../components/PaymentPlans/PaymentPlanCollapsible';
import { expedientStatusLabel } from '@claimup/shared/constants/expedient';

/**
 * Cola de revisión: lo que el agente propone y espera visto bueno.
 *
 * Tres decisiones de interfaz que vienen del diseño y conviene no deshacer:
 *
 * 1. Aprobar es UN clic. Rechazar abre un cuadro de texto obligatorio. La
 *    asimetría es deliberada: si se pide justificar también al aprobar, la
 *    revisión se vuelve un peaje y la gente deja de mirarla.
 * 2. Se cronometra desde que se abre una propuesta hasta que se decide. Si la
 *    mediana se hunde, es que revisar se ha convertido en un trámite — y es la
 *    única alarma que tenemos para detectarlo.
 * 3. Lo primero que se ve es cuánto lleva esperando el más viejo. Un plan
 *    parado no avisa de nada por su cuenta, y "llevamos un mes sin mandar nada
 *    y nadie se ha dado cuenta" es el fallo silencioso de este diseño.
 */

// Dos salidas, sin segundas vueltas (Edgar, 08/10/2026): o se aprueba a la
// primera, o «Lo hago yo» y la persona lo hace desde aquí. Ver AIAgentLoHagoYo.
const VERDICTS = { APPROVED: 'approved', MANUAL: 'manual' };

/** Antigüedad en horas, para decidir si la cola pinta bien o mal. */
function horasDesde(iso) {
  if (!iso) return null;
  return Math.floor((Date.now() - new Date(iso).getTime()) / 3600000);
}

function antiguedadLegible(iso) {
  const h = horasDesde(iso);
  if (h === null) return '—';
  if (h < 1) return 'menos de 1 h';
  if (h < 24) return `${h} h`;
  const d = Math.floor(h / 24);
  return `${d} ${d === 1 ? 'día' : 'días'}`;
}

/** Fecha y hora exactas: es lo que permite ordenar y citar una propuesta. */
function fechaHora(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// Nombres de función para una persona. Quien mira esta pantalla es un gestor,
// no quien escribió el agente.
const EN_CASTELLANO = {
  create_annotation: 'anotar',
  update_expedient_status: 'cambiar el estado',
  create_follow_up_assignment: 'crear seguimiento',
  send_whatsapp: 'enviar WhatsApp',
  send_whatsapp_template: 'enviar WhatsApp',
  send_email: 'enviar email',
  send_sms: 'enviar SMS',
};

/** "Enviar WhatsApp, cambiar el estado y crear seguimiento" */
function resumenAcciones(acciones = []) {
  const nombres = acciones.map(a => EN_CASTELLANO[a.type] || a.type);
  if (!nombres.length) return 'sin acciones';
  const frase =
    nombres.length === 1
      ? nombres[0]
      : `${nombres.slice(0, -1).join(', ')} y ${nombres[nombres.length - 1]}`;
  return frase.charAt(0).toUpperCase() + frase.slice(1);
}

/**
 * La misma cola sirve a los dos que revisan (docs/ar-revision-por-fase-plan.md):
 *   · el admin, en `/ai-agent`: `phase='recovery'` es su trabajo, y
 *     `phase='ar'` es la supervisión de lo AR, que mira pero no decide
 *     (`readOnly`);
 *   · el cliente, en `/ar-review`: lo AR de su empresa. El ámbito lo pone el
 *     servidor; aquí solo cambia la ruta.
 */
/**
 * `clienteAr`: la cola vista por la empresa o su gestor (/ar-review). Sin el
 * filtro de cliente —solo hay uno, el suyo— ni el de línea de WhatsApp: las
 * líneas son los móviles de recobro de ClaimUp (Maytapi), y en AR el WhatsApp
 * sale siempre por el número de Kapso de la empresa. La «línea» que llevaba la
 * tarea («Redmi1») no decía nada de por dónde iba a salir el mensaje.
 */
const AIAgentReview = ({ basePath = '/ai-agent', phase = null, readOnly = false, showSettings = true, clienteAr = false }) => {
  const [planes, setPlanes] = useState([]);
  const [stats, setStats] = useState({ pending: 0, oldest_at: null });
  const [abierto, setAbierto] = useState(null);
  const [previos, setPrevios] = useState([]);
  const [contexto, setContexto] = useState(null);
  const [cargandoCaso, setCargandoCaso] = useState(false);
  // El caso abierto se ha tomado con «Lo hago yo»: la propuesta ya no se puede
  // aprobar y se enseña el panel de acciones.
  const [aMano, setAMano] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verConfig, setVerConfig] = useState(false);
  const [filtroCliente, setFiltroCliente] = useState('');
  const [filtroAccion, setFiltroAccion] = useState('');
  /**
   * El estado del expediente, y este filtra EN EL SERVIDOR — a diferencia de
   * los otros dos.
   *
   * No es una inconsistencia por descuido. Cliente y acción se filtran sobre lo
   * ya cargado porque son atributos del propio plan y la lista trae 50. Pero
   * «primero los Hot» es justo lo que deja de funcionar cuando la cola pasa de
   * 50: filtrar en el navegador enseñaría los Hot **de los primeros 50**, y los
   * de más atrás no existirían para quien revisa. Un filtro que esconde lo que
   * dice enseñar es peor que no tenerlo.
   */
  const [filtroEstado, setFiltroEstado] = useState('');
  // 🔴 Estos dos filtran EN SERVIDOR, no como los de cliente y acción.
  //
  // La cola tiene ~200 propuestas y esta tabla carga 50: filtrar en el navegador
  // contesta sobre un cuarto de la cola pareciendo que contesta sobre toda, que
  // es peor que no filtrar. Los de arriba arrastran ese problema desde que se
  // escribieron; éstos nacen bien.
  //
  // Para qué se pidieron (Edgar, 15/09/2026): ver si hay primeros contactos
  // esperando aprobación de líneas que no han mandado ninguno. Los había.
  const [filtroLinea, setFiltroLinea] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('');
  const [ordenAsc, setOrdenAsc] = useState(true);

  // Los desplegables se construyen con lo que hay en la cola, no con un
  // catálogo fijo: filtrar por un cliente sin propuestas no sirve de nada.
  const clientes = [
    ...new Set(planes.map(p => p.summary?.cliente).filter(Boolean)),
  ].sort((a, b) => a.localeCompare(b));

  const accionesPresentes = [
    ...new Set(planes.flatMap(p => (p.plan?.actions || []).map(a => a.type))),
  ].sort();

  // 🔴 Las opciones de línea y tipo salen del DESGLOSE de la cola entera, que
  // manda el servidor, y no de las 50 filas cargadas. Sacarlas de lo cargado
  // dejaría fuera líneas que sí tienen propuestas esperando — justo las que se
  // buscan cuando uno abre este filtro.
  const desglose = stats.desglose || [];
  const cuentaPorLinea = desglose.reduce((acc, d) => {
    if (!d.phone_id) return acc;
    acc[d.phone_id] = (acc[d.phone_id] || 0) + d.total;
    return acc;
  }, {});

  // 🔴 La línea se llama IGUAL aquí que en la tabla de KPI, y eso era el problema:
  // esta pantalla enseñaba el `phone_id` —«claimup.io», «Persona 1»— y la otra el
  // nombre de la ficha —«LG CV10102», «Iphone Antiguo»—. La misma línea con dos
  // nombres, imposible de cruzar mirando las dos a la vez.
  //
  // ⚠️ Pero el nombre NO es único: hay dos fichas «Redmi1» y dos «Samsung Nuevo»
  // con `phone_id` distintos. Dos opciones idénticas que filtran cosas distintas
  // son peores que el identificador, así que ahí se desempata con él.
  const nombrePorLinea = desglose.reduce((acc, d) => {
    if (d.phone_id && d.nombre) acc[d.phone_id] = d.nombre;
    return acc;
  }, {});
  const vecesQueSeRepite = Object.values(nombrePorLinea).reduce((acc, n) => {
    acc[n] = (acc[n] || 0) + 1;
    return acc;
  }, {});
  const comoSeLlama = id => {
    const n = nombrePorLinea[id];
    if (!n) return id;
    return vecesQueSeRepite[n] > 1 ? `${n} (${id})` : n;
  };

  const lineasEnCola = Object.keys(cuentaPorLinea).sort((a, b) =>
    comoSeLlama(a).localeCompare(comoSeLlama(b))
  );
  const cuentaPorTipo = desglose.reduce((acc, d) => {
    if (!d.task_type) return acc;
    acc[d.task_type] = (acc[d.task_type] || 0) + d.total;
    return acc;
  }, {});
  const tiposEnCola = Object.keys(cuentaPorTipo).sort((a, b) => a.localeCompare(b));

  const visibles = planes
    .filter(p => {
      if (filtroCliente && p.summary?.cliente !== filtroCliente) return false;
      if (filtroAccion && !(p.plan?.actions || []).some(a => a.type === filtroAccion)) return false;
      return true;
    })
    // Por defecto, lo más viejo primero: en una cola de revisión eso es lo que
    // urge, y lo que peor envejece si nadie lo mira.
    .sort((a, b) => {
      const d = new Date(a.created_at) - new Date(b.created_at);
      return ordenAsc ? d : -d;
    });

  // El cronómetro. Arranca al abrir una propuesta, no al cargar la página:
  // lo que se mide es el tiempo de juicio, no el de estar la pestaña abierta.
  // Los filtros que van al SERVIDOR. Se agrupan porque los tres comparten el
  // mismo problema: si no devuelven nada, la barra desaparece con ellos.
  const hayFiltro = Boolean(filtroEstado || filtroLinea || filtroTipo);
  const loQueSeFiltra = [
    filtroEstado && `estado «${filtroEstado}»`,
    filtroLinea && `línea «${comoSeLlama(filtroLinea)}»`,
    filtroTipo && `tipo «${TASK_TYPE_LABELS[filtroTipo] || filtroTipo}»`,
  ]
    .filter(Boolean)
    .join(' y ');
  const limpiarFiltros = () => {
    setFiltroEstado('');
    setFiltroLinea('');
    setFiltroTipo('');
  };

  const abiertoDesde = useRef(null);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const params = { review_status: 'pending', pageSize: 50 };
      if (filtroEstado) params.estados = filtroEstado;
      if (filtroLinea) params.phone_id = filtroLinea;
      if (filtroTipo) params.task_type = filtroTipo;
      if (phase) params.phase = phase;
      const [cola, lista] = await Promise.all([
        phase ? api.get(`${basePath}/review-queue`, { params: { phase } }) : api.get(`${basePath}/review-queue`),
        api.get(`${basePath}/plans`, { params }),
      ]);
      setStats(cola.data.data);
      // Sin ordenar aquí: el orden lo decide la tabla, que es donde el usuario
      // puede cambiarlo.
      setPlanes(lista.data.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [filtroEstado, filtroLinea, filtroTipo, basePath, phase]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const abrir = async plan => {
    if (abierto?._id === plan._id) {
      setAbierto(null);
      return;
    }
    setAbierto(plan);
    setAMano(false);
    setPrevios([]);
    setContexto(null);
    setCargandoCaso(true);
    abiertoDesde.current = Date.now();

    // El expediente y la conversación REALES, más las correcciones previas. Sin
    // esto, quien revisa solo vería el resumen que hizo la propia IA — y revisar
    // a la IA con el relato de la IA no es revisar.
    try {
      const { data } = await api.get(`${basePath}/plans/${plan._id}/review-context`);
      setContexto(data.data.context);
      setPrevios(data.data.previous_rejections || []);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setCargandoCaso(false);
    }
  };

  // Vuelve a leer el caso: tras cada acción a mano cambian la conversación y las
  // tareas abiertas, y es lo que mira quien está haciendo el trabajo.
  const releerCaso = async () => {
    if (!abierto) return;
    try {
      const { data } = await api.get(`${basePath}/plans/${abierto._id}/review-context`);
      setContexto(data.data.context);
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const decidir = async verdict => {
    setEnviando(true);
    try {
      const segundos = abiertoDesde.current
        ? Math.round((Date.now() - abiertoDesde.current) / 1000)
        : null;

      const { data } = await api.post(`${basePath}/plans/${abierto._id}/review`, {
        verdict,
        seconds_spent: segundos,
      });

      if (verdict === VERDICTS.MANUAL) {
        // La tarea del agente ya está cerrada a tu nombre: no queda nada
        // esperando aunque se cierre la página. Ahora se trabaja aquí mismo.
        setAMano(true);
        return;
      }

      const ej = data.data?.execution;
      toast.success(
        ej?.failed
          ? `Aprobado, pero ${ej.failed} de ${ej.executed + ej.failed} acciones fallaron.`
          : 'Aprobado y ejecutado.'
      );
      setAbierto(null);
      cargar();
    } catch (err) {
      // 🔴 Este no es un error cualquiera: es una explicación de por qué esa
      // aprobación no puede salir y qué hacer en su lugar. Un toast de tres
      // segundos no da para leerlo, y si no se lee se vuelve a intentar.
      const esExplicacion = err?.response?.data?.error?.code === 'WHATSAPP_SIN_LINEA';
      toast.error(getErrorMessage(err), esExplicacion ? { autoClose: 15000 } : undefined);
    } finally {
      setEnviando(false);
    }
  };

  const terminado = () => {
    setAMano(false);
    setAbierto(null);
    cargar();
  };

  const horas = horasDesde(stats.oldest_at);
  const colaVieja = horas !== null && horas >= 24;

  return (
    <div>
      <div className='grid grid-cols-1 md:grid-cols-3 gap-3 mb-6'>
        <div className='bg-white rounded-lg border border-slate-200 p-4'>
          <p className='text-xs font-medium text-slate-500 uppercase tracking-wide'>Esperando</p>
          <p className='mt-1 text-2xl font-bold text-slate-800'>{stats.pending}</p>
        </div>
        <div
          className={`rounded-lg border p-4 ${
            colaVieja ? 'bg-red-50 border-red-200' : 'bg-white border-slate-200'
          }`}
        >
          <p className='text-xs font-medium text-slate-500 uppercase tracking-wide'>
            El más viejo lleva
          </p>
          <p
            className={`mt-1 text-2xl font-bold ${colaVieja ? 'text-red-700' : 'text-slate-800'}`}
          >
            {stats.pending ? antiguedadLegible(stats.oldest_at) : '—'}
          </p>
          {colaVieja && (
            <p className='text-xs text-red-700 mt-1'>
              Nadie lo ha mirado en más de un día.
            </p>
          )}
        </div>
        {showSettings && (
          <div className='bg-white rounded-lg border border-slate-200 p-4 flex items-center'>
            <button
              type='button'
              onClick={() => setVerConfig(v => !v)}
              className='text-sm font-medium text-indigo-600 hover:text-indigo-800'
            >
              {verConfig ? 'Ocultar configuración' : 'Qué se revisa y qué no'}
            </button>
          </div>
        )}
      </div>

      {showSettings && verConfig && <AIAgentReviewSettings onChange={cargar} />}

      {loading && <p className='text-sm text-slate-500'>Cargando…</p>}

      {!loading && planes.length === 0 && (
        <div className='bg-white rounded-lg border border-slate-200 p-8 text-center'>
          <p className='text-sm text-slate-600'>
            {hayFiltro
              ? `No hay nada esperando revisión con ${loQueSeFiltra}.`
              : 'No hay nada esperando revisión.'}
          </p>
          {/* 🔴 Sin esto la pantalla se queda atascada: la barra de filtros solo
              aparece cuando hay propuestas, así que un filtro que no devuelve
              nada se lleva por delante el control para quitarlo.
              Vale para los TRES filtros de servidor, no solo para el de estado:
              «una línea sin nada» es justo el resultado que se busca al mirar
              qué líneas tienen primeros contactos parados. */}
          {hayFiltro && (
            <button
              type='button'
              onClick={limpiarFiltros}
              className='mt-3 text-sm font-medium text-blue-600 hover:text-blue-700'
            >
              Quitar los filtros
            </button>
          )}
        </div>
      )}

      {!loading && planes.length > 0 && (
        <div className='flex items-end gap-3 flex-wrap mb-3'>
          {!clienteAr && (
          <div>
            <label className='block text-xs font-medium text-slate-500 mb-1'>Cliente</label>
            <select
              value={filtroCliente}
              onChange={e => setFiltroCliente(e.target.value)}
              className='text-sm border border-slate-300 rounded-lg px-2 py-1.5 bg-white'
            >
              <option value=''>Todos</option>
              {clientes.map(c => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
          )}
          <div>
            <label htmlFor='filtro-accion' className='block text-xs font-medium text-slate-500 mb-1'>
              {clienteAr ? 'Qué propone la IA' : 'Acción'}
            </label>
            <select
              id='filtro-accion'
              value={filtroAccion}
              onChange={e => setFiltroAccion(e.target.value)}
              className='text-sm border border-slate-300 rounded-lg px-2 py-1.5 bg-white'
            >
              <option value=''>Todas</option>
              {accionesPresentes.map(a => (
                <option key={a} value={a}>
                  {EN_CASTELLANO[a] || a}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className='block text-xs font-medium text-slate-500 mb-1'>
              Estado del expediente
            </label>
            <select
              value={filtroEstado}
              onChange={e => setFiltroEstado(e.target.value)}
              className='text-sm border border-slate-300 rounded-lg px-2 py-1.5 bg-white'
            >
              <option value=''>Todos</option>
              {/* «Hot» («Intención de pago») primero y separado: es el que urge, y es el motivo por el
                  que existe este filtro. Un expediente Hot es alguien con
                  conversación viva y con intención de pago. */}
              <option value='Hot'>{expedientStatusLabel('Hot')} — conversación viva</option>
              <option value='Plan de pagos'>Plan de pagos</option>
              <option value='Cobro parcial extrajudicial'>Cobro parcial extrajudicial</option>
              <option value='Dispute'>{expedientStatusLabel('Dispute')}</option>
              <option value='On going'>{expedientStatusLabel('On going')}</option>
              <option value='Email opened'>{expedientStatusLabel('Email opened')}</option>
              <option value='Cold'>{expedientStatusLabel('Cold')}</option>
            </select>
          </div>
          {!clienteAr && (
          <div>
            <label className='block text-xs font-medium text-slate-500 mb-1'>
              Línea de WhatsApp
            </label>
            <select
              aria-label='Línea de WhatsApp'
              value={filtroLinea}
              onChange={e => setFiltroLinea(e.target.value)}
              className='text-sm border border-slate-300 rounded-lg px-2 py-1.5 bg-white'
            >
              <option value=''>Todas</option>
              {/* Con el número de propuestas al lado: así se ve qué línea tiene
                  cosas esperando SIN tener que filtrar una por una. */}
              {lineasEnCola.map(l => (
                <option key={l} value={l}>
                  {comoSeLlama(l)} ({cuentaPorLinea[l]})
                </option>
              ))}
            </select>
          </div>
          )}
          <div>
            <label className='block text-xs font-medium text-slate-500 mb-1'>Tipo de tarea</label>
            <select
              aria-label='Tipo de tarea'
              value={filtroTipo}
              onChange={e => setFiltroTipo(e.target.value)}
              className='text-sm border border-slate-300 rounded-lg px-2 py-1.5 bg-white'
            >
              <option value=''>Todos</option>
              {tiposEnCola.map(tt => (
                <option key={tt} value={tt}>
                  {TASK_TYPE_LABELS[tt] || tt} ({cuentaPorTipo[tt]})
                </option>
              ))}
            </select>
          </div>
          <span className='text-xs text-slate-500 ml-auto pb-2'>
            {visibles.length} de {planes.length}
            {filtroEstado ? ` · ${filtroEstado}` : ''}
            {filtroLinea ? ` · ${comoSeLlama(filtroLinea)}` : ''}
            {filtroTipo ? ` · ${TASK_TYPE_LABELS[filtroTipo] || filtroTipo}` : ''}
          </span>
        </div>
      )}

      {!loading && visibles.length > 0 && (
        <div className='hidden md:grid grid-cols-12 gap-2 px-4 py-2 text-xs font-semibold uppercase text-slate-500 bg-slate-50 border border-slate-200 rounded-t-lg'>
          <div className='col-span-3'>Deudor / cliente</div>
          <div className='col-span-2'>Deuda / estado</div>
          <div className='col-span-2'>Tarea</div>
          <div className='col-span-2'>Qué propone</div>
          <div className='col-span-3 text-right'>
            <button
              type='button'
              onClick={() => setOrdenAsc(v => !v)}
              className='uppercase font-semibold hover:text-slate-800'
            >
              Fecha {ordenAsc ? '↑ antiguas' : '↓ recientes'}
            </button>
          </div>
        </div>
      )}

      <div className='space-y-3'>
        {visibles.map(plan => {
          const acciones = plan.plan?.actions || [];
          const estaAbierto = abierto?._id === plan._id;
          const s = plan.summary || {};

          return (
            <div key={plan._id} className='bg-white rounded-lg border border-slate-200'>
              <button
                type='button'
                onClick={() => abrir(plan)}
                className='w-full text-left px-4 py-3 hover:bg-slate-50'
              >
                <div className='grid grid-cols-12 gap-2 items-center'>
                  <div className='col-span-12 md:col-span-3'>
                    <p className='text-sm font-medium text-slate-800 truncate'>
                      {s.deudor || 'Sin deudor'}
                    </p>
                    {!clienteAr && <p className='text-xs text-slate-500 truncate'>{s.cliente || '—'}</p>}
                  </div>
                  <div className='col-span-6 md:col-span-2 text-xs text-slate-600'>
                    {typeof s.importe_deuda === 'number'
                      ? `${s.importe_deuda.toLocaleString('es-ES')} €`
                      : '—'}
                    <span className='block text-slate-400'>{s.estado || '—'}</span>
                  </div>
                  <div className='col-span-6 md:col-span-2 text-xs text-slate-600'>
                    {TASK_TYPE_LABELS[plan.situation?.task_type] ||
                      plan.situation?.task_type ||
                      '—'}
                    {plan.situation?.peldano > 0 && (
                      <span className='block text-slate-400'>peldaño {plan.situation.peldano}</span>
                    )}
                  </div>
                  <div className='col-span-6 md:col-span-2 text-xs text-slate-700'>
                    {resumenAcciones(acciones)}
                    {plan.attempt > 1 && (
                      <span className='block text-amber-700'>
                        corregido {plan.attempt - 1}{' '}
                        {plan.attempt - 1 === 1 ? 'vez' : 'veces'}
                      </span>
                    )}
                  </div>
                  <div className='col-span-12 md:col-span-3 text-xs text-slate-500 md:text-right'>
                    {fechaHora(plan.created_at)}
                    <span className='block text-slate-400'>
                      hace {antiguedadLegible(plan.created_at)}
                    </span>
                  </div>
                </div>
              </button>

              {estaAbierto && (
                <div className='border-t border-slate-200 p-4'>
                  <AIAgentReviewCase
                    plan={plan}
                    contexto={contexto}
                    rechazosPrevios={previos}
                    cargando={cargandoCaso}
                    aMano={aMano}
                  />

                  <div className='h-4' />

                  {/* Antes de decidir, y para todos, también en supervisión:
                      consultar no decide nada (docs/consultar-el-caso-plan.md). */}
                  <AIAgentConsult planId={String(plan._id)} basePath={basePath} />

                  <div className='h-4' />

                  {readOnly ? (
                    <p className='text-sm text-slate-500'>
                      Supervisión: esta propuesta es de un expediente AR y la revisa el cliente.
                    </p>
                  ) : aMano ? (
                    <AIAgentLoHagoYo
                      plan={plan}
                      contexto={contexto}
                      onCambio={releerCaso}
                      onTerminado={terminado}
                      basePath={basePath}
                    />
                  ) : (
                    <div className='flex items-center gap-2 flex-wrap'>
                      <button
                        type='button'
                        disabled={enviando}
                        onClick={() => decidir(VERDICTS.APPROVED)}
                        className='px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-50'
                      >
                        Aprobar y ejecutar
                      </button>
                      <button
                        type='button'
                        disabled={enviando}
                        onClick={() => decidir(VERDICTS.MANUAL)}
                        className='px-4 py-2 rounded-lg border border-indigo-300 text-sm font-medium text-indigo-700 hover:bg-indigo-50 disabled:opacity-50'
                      >
                        Lo hago yo
                      </button>
                    </div>
                  )}

                  {/* El plan de pagos se acuerda muchas veces aquí, contestando al
                      deudor (docs/planes-de-pago-fase-1-plan.md, §7). Plegado: no
                      es lo que se viene a decidir, y quién puede lo dice el servidor. */}
                  {!readOnly && plan.expedient_id && (
                    <PaymentPlanCollapsible
                      expedientId={String(plan.expedient_id)}
                      debt={contexto?.expedient?.importe_deuda}
                      onStateChange={releerCaso}
                    />
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AIAgentReview;
